import { useCallback, useEffect, useState } from "react";
import { getWorkerApplication } from "../workerApplications/workerApplicationsApi.js";

/**
 * Prompt 138W-R4/R5: fetches a single Worker Application's real detail
 * record for the shared applicant drawer (ApplicantDetailDrawer.jsx) -
 * reused by AgentWorkersPage, AgentOrgChartPage, and the Agent Dashboard's
 * own applicant list so "click an applicant" behaves identically
 * everywhere, without duplicating this fetch-on-select effect three times.
 *
 * `refetch` (Prompt 138W-R5) re-pulls the same record without resetting
 * `data` to null first (mirrors useWorkerProfilesList.js) - used right
 * after a successful "เริ่ม e-KYC" call so the stepper/badge update to the
 * new real applicationStatus instead of the drawer blanking out mid-read.
 */
export function useAgentApplicantDetail(applicationId) {
  const [state, setState] = useState({ status: "idle", data: null, error: null });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!applicationId) {
      setState({ status: "idle", data: null, error: null });
      return undefined;
    }
    let cancelled = false;
    setState((previous) => ({ status: "loading", data: previous.data, error: null }));
    getWorkerApplication(applicationId)
      .then((data) => {
        if (!cancelled) {
          setState({ status: "ready", data, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState((previous) => ({ status: "error", data: previous.data, error }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [applicationId, reloadToken]);

  const refetch = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, refetch };
}
